import { useLocale } from '@ecomm/data-hooks'
import { useOnClickOutside, useOnKeyUp } from '@ecomm/shared-hooks'
import { ChevronDown } from '@ecomm/shared-icons'
import { useRef, useState } from 'react'
import { COUNTRIES } from './CountrySelectorConst'
import { CountrySelectorDropDown } from './CountrySelectorDropDown'
import { CountrySelectorFlagIcon } from './CountrySelectorFlagIcon'

/**
 * Country selector component.
 * Allows users to select their country from a dropdown list.
 * Displays the current country with its flag and label.
 * Used in the site footer.
 */
export function CountrySelector() {
  const locale = useLocale()
  const { isOpen, ref, handleClick } = useCountrySelector()
  const { alt, label, src } = COUNTRIES[locale]

  return (
    <div
      className="flex justify-start md:basis-1/3 md:!items-center md:!justify-end lg:basis-1/6 lg:!items-baseline xl:basis-1/12"
      ref={ref}
      data-testid="country-selector"
    >
      <div className="inline-block relative py-2.5">
        <CountrySelectorDropDown isOpen={isOpen} />
        <button
          className="flex items-center p-0 m-0 border-0 text-white bg-transparent cursor-pointer"
          onClick={handleClick}
          data-testid="countrySelectorButton"
        >
          <span className="sr-only">{alt}</span>

          <div className="flex items-baseline gap-1.5">
            <CountrySelectorFlagIcon src={src} alt={alt} />
            <span className="text-base hover:underline ml-2">{label}</span>
            <ChevronDown
              className={`m-auto h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </div>
        </button>
      </div>
    </div>
  )
}

/**
 * Hook to manage the state and behavior of the country selector.
 */
const useCountrySelector = () => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useClose(setIsOpen, ref)

  const handleClick = () => {
    setIsOpen(prev => !prev)
  }

  return { isOpen, ref, handleClick }
}

/**
 * Hook to handle closing the country selector dropdown for non direct clicking.
 */
const useClose = (
  setIsOpen: (isOpen: boolean) => void,
  ref: React.RefObject<HTMLDivElement>
) => {
  useOnClickOutside(() => {
    setIsOpen(false)
  }, ref)

  useOnKeyUp('Escape', () => {
    setIsOpen(false)
  })
}
